import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { NoteImageBlock } from "./NoteImageBlock";
import { cn } from "../lib/utils";
import { createNoteEditorHistory } from "../lib/noteEditorHistory";
import {
  expandPasteIntoBlocks,
  mergeBlockWithPrevious,
  splitBlockText,
  unformatFirstEmptyBlock,
} from "../lib/noteEditorInsert";
import {
  getEditorSelectionOffsets,
  getMarkdownSelectionOffsets,
  htmlToMarkdown,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  isEntireEditorContentsSelected,
  markdownToHtml,
  selectAllEditorContents,
  setEditorSelectionOffset,
  splitMarkdownAt,
} from "../lib/noteInlineMarkdown";
import {
  blockIndexInRange,
  getFormatBlockRange,
  normalizeBlockRange,
} from "../lib/noteEditorSelection";
import {
  blockHasMultipleLines,
  blockEditableSource,
  formatEntireBlockAsLines,
  formatSelectedLinesInBlock,
  getActiveEditorContext,
  getSelectedLineRangeInText,
  replaceBlocksSlice,
} from "../lib/noteBlockLines";
import {
  convertBlockType,
  createEmptyBlock,
  isEmptyBlock,
  isNoteEditorEmpty,
  parseNoteContent,
  serializeNoteContent,
} from "../lib/noteBlocks";

function numberedIndex(blocks, index) {
  let n = 1;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (blocks[i].type === "numbered") n += 1;
    else break;
  }
  return n;
}

function getActiveBlockIndex(root, fallback = 0) {
  const active = document.activeElement;
  if (!active || !root?.contains(active)) return fallback;
  const blockEl = active.closest("[data-note-block-index]");
  if (!blockEl) return fallback;
  const index = Number(blockEl.dataset.noteBlockIndex);
  return Number.isNaN(index) ? fallback : index;
}

function EditableText({
  value,
  onChange,
  className,
  placeholder,
  showPlaceholder = false,
  onFocus,
  multiline = true,
  richText = false,
  readOnly = false,
  field = "text",
  onMouseDown,
  onMergePrevious,
  onEnter,
  onPaste,
  onNavigate,
  onUndo,
  onRedo,
  onSelectAllBlocks,
  onSelectBlockOnly,
  onDeleteSelectedBlocks,
  "data-testid": testId,
}) {
  const ref = useRef(null);

  const readDomValue = useCallback(() => {
    const el = ref.current;
    if (!el) return "";
    return richText ? htmlToMarkdown(el.innerHTML ?? "") : el.textContent ?? "";
  }, [richText]);

  const getOffsets = useCallback(() => {
    const el = ref.current;
    if (!el) return null;
    return getEditorSelectionOffsets(el, { richText });
  }, [richText]);

  const writeDomValue = useCallback(
    (nextValue) => {
      const el = ref.current;
      if (!el) return;
      if (richText) {
        el.innerHTML = markdownToHtml(nextValue || "");
        return;
      }
      el.textContent = nextValue || "";
    },
    [richText],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stateValue = value || "";
    const domValue = richText ? htmlToMarkdown(el.innerHTML ?? "") : el.textContent ?? "";
    if (domValue === stateValue) return;
    writeDomValue(stateValue);
  }, [richText, value, writeDomValue]);

  const handleInput = () => {
    onChange(readDomValue());
  };

  const handleKeyDown = (e) => {
    if (!readOnly && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      e.stopPropagation();
      const el = ref.current;
      const plainText = readDomValue();
      const entireBlockSelected = isEntireEditorContentsSelected(el, plainText, { richText });
      if (!entireBlockSelected) {
        selectAllEditorContents(el);
        onSelectBlockOnly?.();
      } else {
        onSelectAllBlocks?.();
      }
      return;
    }

    if (!readOnly && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) onRedo?.();
      else onUndo?.();
      return;
    }

    if (!readOnly && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "y") {
      e.preventDefault();
      e.stopPropagation();
      onRedo?.();
      return;
    }

    if (
      onNavigate &&
      !readOnly &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown")
    ) {
      const offsets = getOffsets();
      if (offsets && offsets.start === offsets.end) {
        const plainText = readDomValue();
        const onFirstLine = multiline ? isCaretOnFirstLine(ref.current) : true;
        const onLastLine = multiline ? isCaretOnLastLine(ref.current) : true;
        const atTextStart = offsets.start === 0;
        const atTextEnd = offsets.start === plainText.length;
        const direction = e.key.slice(5).toLowerCase();
        const shouldNavigate =
          (direction === "left" && atTextStart) ||
          (direction === "right" && atTextEnd) ||
          (direction === "up" && onFirstLine && (!multiline || atTextStart)) ||
          (direction === "down" && onLastLine && (!multiline || atTextEnd));

        if (shouldNavigate && onNavigate(direction, { atTextStart, atTextEnd, onFirstLine, onLastLine })) {
          e.preventDefault();
          return;
        }
      }
    }

    if (e.key === "Enter" && e.shiftKey && !readOnly) {
      e.preventDefault();
      const plainText = readDomValue();
      const offsets = getOffsets();
      const cursor = offsets?.start ?? plainText.length;
      const next = `${plainText.slice(0, cursor)}\n${plainText.slice(cursor)}`;
      onChange(next);
      requestAnimationFrame(() => {
        setEditorSelectionOffset(ref.current, cursor + 1, { richText });
      });
      return;
    }

    if (e.key === "Enter" && !e.shiftKey && onEnter) {
      e.preventDefault();
      const plainText = readDomValue();
      const offsets = getOffsets();
      onEnter({
        cursor: offsets?.start ?? plainText.length,
        plainText,
      });
      return;
    }

    if (e.key === "Enter" && !e.shiftKey && multiline && !readOnly) {
      e.preventDefault();
      const plainText = readDomValue();
      const offsets = getOffsets();
      const cursor = offsets?.start ?? plainText.length;
      const next = `${plainText.slice(0, cursor)}\n${plainText.slice(cursor)}`;
      onChange(next);
      requestAnimationFrame(() => {
        setEditorSelectionOffset(ref.current, cursor + 1, { richText });
      });
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      return;
    }
    if (
      !readOnly &&
      (e.key === "Backspace" || e.key === "Delete") &&
      !e.metaKey &&
      !e.ctrlKey &&
      onDeleteSelectedBlocks?.()
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if ((e.key === "Backspace" || e.key === "Delete") && onMergePrevious) {
      const plainText = readDomValue();
      const offsets = getOffsets();
      const atStart = (offsets?.start ?? 0) === 0;
      const empty = !plainText.trim();
      const shouldMerge = empty || (e.key === "Backspace" && atStart);

      if (shouldMerge) {
        e.preventDefault();
        onMergePrevious({ plainText, atStart: empty || atStart });
      }
    }
  };

  const handlePaste = (e) => {
    if (readOnly) return;
    const pastedText = e.clipboardData?.getData("text/plain") ?? "";
    if (!pastedText) return;

    if (onPaste) {
      e.preventDefault();
      const plainText = readDomValue();
      const offsets = getOffsets();
      onPaste({
        cursor: offsets?.start ?? plainText.length,
        plainText,
        pastedText,
      });
      return;
    }

    if (multiline) {
      e.preventDefault();
      document.execCommand("insertText", false, pastedText);
    }
  };

  return (
    <div
      ref={ref}
      role="textbox"
      contentEditable={!readOnly}
      suppressContentEditableWarning
      data-note-block-field={field}
      data-placeholder={showPlaceholder ? placeholder : ""}
      data-testid={testId}
      className={cn(
        "min-w-0 flex-1 outline-none cursor-text",
        multiline && "min-h-[1.5em] whitespace-pre-wrap break-words",
        !multiline && "min-h-[1.75rem]",
        richText && "[&_strong]:font-semibold [&_em]:italic",
        showPlaceholder &&
          "empty:before:content-[attr(data-placeholder)] empty:before:text-[var(--t-meta)]",
        className,
      )}
      onInput={readOnly ? undefined : handleInput}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      onPaste={readOnly ? undefined : handlePaste}
      onKeyDown={readOnly ? undefined : handleKeyDown}
    />
  );
}

function NoteBlockView({
  block,
  index,
  blocks,
  readOnly,
  showPlaceholders,
  onFocus,
  onChange,
  onUnformat,
  onMergePrevious,
  onEnter,
  onPaste,
  onNavigate,
  editorCommands,
}) {
  const focusProps = { onFocus: () => onFocus(index), showPlaceholder: showPlaceholders, richText: true };
  const commandProps = editorCommands || {};
  const mergeProps = readOnly ? {} : { onMergePrevious: (payload) => onMergePrevious(index, payload) };
  const enterProps = readOnly
    ? {}
    : { onEnter: (payload) => onEnter(index, payload), onPaste: (payload) => onPaste(index, payload) };
  const navigateProps = readOnly || !onNavigate
    ? {}
    : {
        onNavigate: (direction, ctx) => onNavigate(index, "text", direction, ctx),
      };
  const navigateTitleProps = readOnly || !onNavigate
    ? {}
    : {
        onNavigate: (direction, ctx) => onNavigate(index, "title", direction, ctx),
      };
  const navigateBodyProps = readOnly || !onNavigate
    ? {}
    : {
        onNavigate: (direction, ctx) => onNavigate(index, "body", direction, ctx),
      };

  switch (block.type) {
    case "h1":
      return (
        <EditableText
          {...focusProps}
          {...commandProps}
          {...mergeProps}
          {...enterProps}
          {...navigateProps}
          value={block.text}
          onChange={(text) => onChange(index, { ...block, text })}
          readOnly={readOnly}
          className="font-heading text-2xl tracking-tight text-[var(--t-text)]"
          placeholder="Heading 1"
          data-testid="note-block-h1"
        />
      );
    case "h2":
      return (
        <EditableText
          {...focusProps}
          {...commandProps}
          {...mergeProps}
          {...enterProps}
          {...navigateProps}
          value={block.text}
          onChange={(text) => onChange(index, { ...block, text })}
          readOnly={readOnly}
          className="font-heading text-xl tracking-tight text-[var(--t-text)]"
          placeholder="Heading 2"
          data-testid="note-block-h2"
        />
      );
    case "h3":
      return (
        <EditableText
          {...focusProps}
          {...commandProps}
          {...mergeProps}
          {...enterProps}
          {...navigateProps}
          value={block.text}
          onChange={(text) => onChange(index, { ...block, text })}
          readOnly={readOnly}
          className="font-heading text-lg tracking-tight text-[var(--t-text)]"
          placeholder="Heading 3"
          data-testid="note-block-h3"
        />
      );
    case "h4":
      return (
        <EditableText
          {...focusProps}
          {...commandProps}
          {...mergeProps}
          {...enterProps}
          {...navigateProps}
          value={block.text}
          onChange={(text) => onChange(index, { ...block, text })}
          readOnly={readOnly}
          className="font-heading text-base tracking-tight text-[var(--t-text)]"
          placeholder="Heading 4"
          data-testid="note-block-h4"
        />
      );
    case "bullet":
      return (
        <div className="flex items-center gap-2" data-testid="note-block-bullet">
          <span className="shrink-0 text-sm leading-none text-[var(--t-meta)] select-none" aria-hidden>
            •
          </span>
          <EditableText
            {...focusProps}
            {...commandProps}
            {...mergeProps}
            {...enterProps}
            {...navigateProps}
            value={block.text}
            onChange={(text) => onChange(index, { ...block, text })}
            readOnly={readOnly}
            className="text-xs sm:text-sm font-light leading-normal text-[var(--t-text)]"
            placeholder="List item"
          />
        </div>
      );
    case "numbered":
      return (
        <div className="flex items-center gap-2" data-testid="note-block-numbered">
          <span className="w-5 shrink-0 text-right text-xs leading-none text-[var(--t-meta)] tabular-nums select-none" aria-hidden>
            {numberedIndex(blocks, index)}.
          </span>
          <EditableText
            {...focusProps}
            {...commandProps}
            {...mergeProps}
            {...enterProps}
            {...navigateProps}
            value={block.text}
            onChange={(text) => onChange(index, { ...block, text })}
            readOnly={readOnly}
            className="text-xs sm:text-sm font-light leading-normal text-[var(--t-text)]"
            placeholder="List item"
          />
        </div>
      );
    case "todo":
      return (
        <div className="flex min-h-[1.25rem] items-center gap-2" data-testid="note-block-todo">
          <Checkbox
            checked={block.checked}
            disabled={readOnly}
            onCheckedChange={(checked) => onChange(index, { ...block, checked: checked === true })}
            className="mt-0 shrink-0 border-[var(--t-border)] data-[state=checked]:bg-[var(--t-accent)] data-[state=checked]:border-[var(--t-accent)]"
            onClick={(e) => e.stopPropagation()}
          />
          <EditableText
            {...focusProps}
            {...commandProps}
            {...mergeProps}
            {...enterProps}
            {...navigateProps}
            value={block.text}
            onChange={(text) => onChange(index, { ...block, text })}
            readOnly={readOnly}
            className="!min-h-0 flex-1 py-0 text-xs sm:text-sm font-light leading-5 text-[var(--t-text)]"
            placeholder=""
          />
        </div>
      );
    case "toggle":
      return (
        <details className="group/toggle w-full" open data-testid="note-block-toggle">
          <summary className="flex cursor-pointer list-none items-start gap-1.5 marker:content-none [&::-webkit-details-marker]:hidden">
            <ChevronRight
              size={14}
              strokeWidth={1.4}
              className="mt-0.5 shrink-0 text-[var(--t-meta)] transition-transform group-open/toggle:rotate-90"
            />
            <EditableText
              {...focusProps}
              {...commandProps}
              {...mergeProps}
              {...enterProps}
              {...navigateTitleProps}
              field="title"
              value={block.title}
              onChange={(title) => onChange(index, { ...block, title })}
              readOnly={readOnly}
              className="text-xs sm:text-sm font-light leading-normal text-[var(--t-text)]"
              placeholder="Toggle"
              onMouseDown={(e) => e.stopPropagation()}
            />
          </summary>
          <div className="mt-2 pl-5">
            <EditableText
              {...focusProps}
              {...commandProps}
              {...enterProps}
              value={block.body}
              onChange={(body) => onChange(index, { ...block, body })}
              onFocus={() => onFocus(index)}
              onMergePrevious={
                readOnly ? undefined : (payload) => onMergePrevious(index, { ...payload, field: "body" })
              }
              {...navigateBodyProps}
              field="body"
              readOnly={readOnly}
              multiline
              richText
              showPlaceholder={showPlaceholders}
              className="text-xs sm:text-sm font-light leading-normal text-[var(--t-meta)]"
              placeholder="Write your thoughts"
            />
          </div>
        </details>
      );
    case "quote":
      return (
        <blockquote
          className="border-l-2 border-[var(--t-border)] pl-4 text-xs sm:text-sm font-light italic text-[var(--t-meta)]"
          data-testid="note-block-quote"
        >
          <EditableText
            {...focusProps}
            {...commandProps}
            {...mergeProps}
            {...enterProps}
            {...navigateProps}
            value={block.text}
            onChange={(text) => onChange(index, { ...block, text })}
            readOnly={readOnly}
            multiline
            richText
            className="italic"
            placeholder="Quote"
          />
        </blockquote>
      );
    case "callout":
      return (
        <div
          className="rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] bg-[#FAFBFC] px-3 py-3"
          data-testid="note-block-callout"
        >
          <EditableText
            {...focusProps}
            {...commandProps}
            {...mergeProps}
            {...enterProps}
            {...navigateProps}
            value={block.text}
            onChange={(text) => onChange(index, { ...block, text })}
            readOnly={readOnly}
            multiline
            richText
            className="text-xs sm:text-sm font-light leading-normal text-[var(--t-text)]"
            placeholder="Callout"
          />
        </div>
      );
    case "image":
      return (
        <div onFocus={() => onFocus(index)} data-testid="note-block-image-wrap">
          <NoteImageBlock fileId={block.fileId} alt={block.alt} />
        </div>
      );
    case "divider":
      return (
        <hr
          tabIndex={readOnly ? -1 : 0}
          className="border-0 border-t border-[var(--t-border)] my-2 outline-none focus-visible:ring-1 focus-visible:ring-[var(--t-accent)]"
          data-testid="note-block-divider"
          onFocus={() => onFocus(index)}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === "Backspace" || e.key === "Delete") {
                    e.preventDefault();
                    onMergePrevious(index, { plainText: "", atStart: true });
                    return;
                  }
                  if (
                    onNavigate &&
                    (e.key === "ArrowUp" ||
                      e.key === "ArrowLeft" ||
                      e.key === "ArrowDown" ||
                      e.key === "ArrowRight")
                  ) {
                    const direction = e.key.slice(5).toLowerCase();
                    if (onNavigate(index, "divider", direction, { atTextStart: true, atTextEnd: true, onFirstLine: true, onLastLine: true })) {
                      e.preventDefault();
                    }
                  }
                }
          }
        />
      );
    default:
      return (
        <EditableText
          {...focusProps}
          {...commandProps}
          {...mergeProps}
          {...enterProps}
          {...navigateProps}
          value={block.text}
          onChange={(text) => onChange(index, { ...block, text })}
          readOnly={readOnly}
          className="text-xs sm:text-sm font-light text-[var(--t-text)]"
          placeholder="Start writing…"
          data-testid="note-block-paragraph"
        />
      );
  }
}

export const NoteRichEditor = forwardRef(function NoteRichEditor(
  { noteId, value, onChange, readOnly = false, fillContainer = false, toolbar, className },
  ref,
) {
  const [blocks, setBlocks] = useState(() => parseNoteContent(value));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [blockSelection, setBlockSelection] = useState(null);
  const focusedIndexRef = useRef(0);
  const selectionAnchorRef = useRef(0);
  const syncingRef = useRef(false);
  const historyRef = useRef(createNoteEditorHistory());
  const historyActionRef = useRef(false);
  const typingHistoryTimerRef = useRef(null);
  const blocksRef = useRef(blocks);
  const blockSelectionRef = useRef(blockSelection);
  const editorContainerRef = useRef(null);

  blocksRef.current = blocks;
  blockSelectionRef.current = blockSelection;
  focusedIndexRef.current = focusedIndex;

  const emitChange = useCallback(
    (next) => {
      if (!syncingRef.current) onChange(serializeNoteContent(next));
    },
    [onChange],
  );

  const setFocusedBlock = useCallback((index) => {
    focusedIndexRef.current = index;
    setFocusedIndex(index);
  }, []);

  const clearBlockSelection = useCallback(() => {
    setBlockSelection(null);
  }, []);

  const selectAllBlocks = useCallback(() => {
    const len = blocksRef.current.length;
    if (!len) return;
    setBlockSelection({ start: 0, end: len - 1 });
    selectionAnchorRef.current = 0;
    window.getSelection()?.removeAllRanges();
  }, []);

  const selectBlockOnly = useCallback(() => {
    clearBlockSelection();
  }, [clearBlockSelection]);

  const handleBlockMouseDown = useCallback(
    (e, index) => {
      if (e.shiftKey) {
        e.preventDefault();
        const anchor = selectionAnchorRef.current ?? focusedIndexRef.current;
        selectionAnchorRef.current = anchor;
        setBlockSelection(normalizeBlockRange(anchor, index));
        setFocusedBlock(index);
        return;
      }
      selectionAnchorRef.current = index;
      clearBlockSelection();
    },
    [clearBlockSelection, setFocusedBlock],
  );

  const extendBlockSelection = useCallback((index) => {
    const anchor = selectionAnchorRef.current ?? focusedIndexRef.current;
    selectionAnchorRef.current = anchor;
    setBlockSelection(normalizeBlockRange(anchor, index));
  }, []);

  const focusBlockEditor = useCallback((index, { atEnd = false, cursor = null, field = "text" } = {}) => {
    requestAnimationFrame(() => {
      const root = editorContainerRef.current;
      if (!root) return;
      let el;
      if (field === "divider") {
        el = root.querySelector(`[data-note-block-index="${index}"] hr[tabindex="0"]`);
      } else if (field === "title" || field === "body") {
        el = root.querySelector(`[data-note-block-index="${index}"] [data-note-block-field="${field}"]`);
      } else {
        el = root.querySelector(`[data-note-block-index="${index}"] [contenteditable="true"]`);
      }
      if (!el) {
        el = root.querySelector(
          `[data-note-block-index="${index}"] [contenteditable="true"], [data-note-block-index="${index}"] hr[tabindex="0"]`,
        );
      }
      if (!el) return;
      el.focus();
      if (el.isContentEditable) {
        if (cursor != null) {
          setEditorSelectionOffset(el, cursor, { richText: true });
        } else if (atEnd) {
          setEditorSelectionOffset(el, Number.MAX_SAFE_INTEGER, { richText: true });
        }
      }
    });
  }, []);

  const deleteSelectedBlocks = useCallback(() => {
    const selection = blockSelectionRef.current;
    if (!selection) return false;

    setBlocks((prev) => {
      if (!prev.length) return prev;

      historyRef.current.push(prev);

      const start = Math.max(0, Math.min(selection.start, prev.length - 1));
      const end = Math.max(0, Math.min(selection.end, prev.length - 1));
      const deletingAll = start === 0 && end >= prev.length - 1;

      let next;
      if (deletingAll) {
        next = [createEmptyBlock("paragraph")];
      } else {
        next = [...prev.slice(0, start), ...prev.slice(end + 1)];
        if (!next.length) next = [createEmptyBlock("paragraph")];
      }

      emitChange(next);
      const focusIndex = Math.min(start, next.length - 1);
      focusedIndexRef.current = focusIndex;
      setFocusedIndex(focusIndex);
      setBlockSelection(null);
      selectionAnchorRef.current = focusIndex;
      focusBlockEditor(focusIndex);
      return next;
    });

    return true;
  }, [emitChange, focusBlockEditor]);

  const navigateFromBlock = useCallback(
    (index, field, direction) => {
      const blocks = blocksRef.current;
      const block = blocks[index];
      if (!block) return false;

      if (block.type === "toggle") {
        if (field === "title" && direction === "down") {
          setFocusedBlock(index);
          focusBlockEditor(index, { cursor: 0, field: "body" });
          return true;
        }
        if (field === "body" && direction === "up") {
          setFocusedBlock(index);
          focusBlockEditor(index, { atEnd: true, field: "title" });
          return true;
        }
      }

      const step = direction === "left" || direction === "up" ? -1 : 1;
      const targetIndex = index + step;
      if (targetIndex < 0 || targetIndex >= blocks.length) return false;

      const targetBlock = blocks[targetIndex];
      setFocusedBlock(targetIndex);

      if (targetBlock.type === "divider") {
        focusBlockEditor(targetIndex, { field: "divider" });
        return true;
      }

      if (step < 0) {
        if (targetBlock.type === "toggle") {
          focusBlockEditor(targetIndex, { atEnd: true, field: "body" });
        } else {
          focusBlockEditor(targetIndex, { atEnd: true });
        }
      } else if (targetBlock.type === "toggle") {
        focusBlockEditor(targetIndex, { cursor: 0, field: "title" });
      } else {
        focusBlockEditor(targetIndex, { cursor: 0 });
      }

      return true;
    },
    [focusBlockEditor, setFocusedBlock],
  );

  const commitBlocks = useCallback(
    (updater, { recordHistory = true, focusIndex = null, focusOpts = {} } = {}) => {
      setBlocks((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (recordHistory && !syncingRef.current && !historyActionRef.current) {
          historyRef.current.push(prev);
        }
        emitChange(next);
        if (focusIndex != null) {
          focusedIndexRef.current = focusIndex;
          setFocusedIndex(focusIndex);
          focusBlockEditor(focusIndex, focusOpts);
        }
        return next;
      });
    },
    [emitChange, focusBlockEditor],
  );

  const scheduleTypingHistory = useCallback(() => {
    if (typingHistoryTimerRef.current) return;
    historyRef.current.push(blocksRef.current);
    typingHistoryTimerRef.current = window.setTimeout(() => {
      typingHistoryTimerRef.current = null;
    }, 700);
  }, []);

  const undo = useCallback(() => {
    setBlocks((prev) => {
      const restored = historyRef.current.undo(prev);
      if (!restored) return prev;
      historyActionRef.current = true;
      emitChange(restored);
      queueMicrotask(() => {
        historyActionRef.current = false;
      });
      return restored;
    });
  }, [emitChange]);

  const redo = useCallback(() => {
    setBlocks((prev) => {
      const restored = historyRef.current.redo(prev);
      if (!restored) return prev;
      historyActionRef.current = true;
      emitChange(restored);
      queueMicrotask(() => {
        historyActionRef.current = false;
      });
      return restored;
    });
  }, [emitChange]);

  const editorCommands = useMemo(
    () => ({
      onUndo: undo,
      onRedo: redo,
      onSelectAllBlocks: selectAllBlocks,
      onSelectBlockOnly: selectBlockOnly,
      onDeleteSelectedBlocks: deleteSelectedBlocks,
    }),
    [deleteSelectedBlocks, redo, selectAllBlocks, selectBlockOnly, undo],
  );

  const applyInlineFormat = useCallback((command) => {
    const root = editorContainerRef.current;
    const active = document.activeElement;
    if (!root || !active?.isContentEditable || !root.contains(active)) return;
    historyRef.current.push(blocksRef.current);
    document.execCommand(command === "bold" ? "bold" : "italic", false, null);
    active.dispatchEvent(new Event("input", { bubbles: true }));
  }, []);

  const splitBlockAtCursor = useCallback(
    (index, { cursor, plainText }) => {
      setBlocks((prev) => {
        const block = prev[index];
        if (!block || block.type === "divider") return prev;

        historyRef.current.push(prev);

        const activeField = document.activeElement?.dataset?.noteBlockField;
        let current;
        let next;

        if (block.type === "toggle" && activeField === "body") {
          const { before, after } = splitMarkdownAt(plainText ?? "", cursor);
          current = { ...block, body: before };
          next = after ? { type: "paragraph", text: after } : createEmptyBlock("paragraph");
        } else {
          ({ current, next } = splitBlockText(block, cursor, plainText));
        }

        const nextBlocks = [...prev];
        nextBlocks[index] = current;
        nextBlocks.splice(index + 1, 0, next);

        emitChange(nextBlocks);
        focusedIndexRef.current = index + 1;
        setFocusedIndex(index + 1);
        focusBlockEditor(index + 1, { atEnd: isEmptyBlock(next) });
        return nextBlocks;
      });
    },
    [emitChange, focusBlockEditor],
  );

  const insertPasteAtCursor = useCallback(
    (index, { cursor, plainText, pastedText }) => {
      setBlocks((prev) => {
        const block = prev[index];
        if (!block || block.type === "divider") return prev;

        historyRef.current.push(prev);

        const { blocks: replacement, focusIndex, cursor: focusCursor } = expandPasteIntoBlocks(
          block,
          cursor,
          plainText,
          pastedText,
        );

        const next = [...prev];
        next.splice(index, 1, ...replacement);

        emitChange(next);
        const absoluteFocus = index + focusIndex;
        focusedIndexRef.current = absoluteFocus;
        setFocusedIndex(absoluteFocus);
        focusBlockEditor(absoluteFocus, { cursor: focusCursor });
        return next;
      });
    },
    [emitChange, focusBlockEditor],
  );

  const focusAtEnd = useCallback(() => {
    setBlocks((prev) => {
      historyRef.current.push(prev);
      const last = prev[prev.length - 1];
      let next = prev;
      let focusIdx = prev.length - 1;
      if (!last || last.type === "divider" || !isEmptyBlock(last)) {
        next = [...prev, createEmptyBlock("paragraph")];
        focusIdx = next.length - 1;
      }
      emitChange(next);
      focusedIndexRef.current = focusIdx;
      setFocusedIndex(focusIdx);
      focusBlockEditor(focusIdx, { atEnd: true });
      return next;
    });
  }, [emitChange, focusBlockEditor]);

  const handleEditorMouseDown = useCallback(
    (e) => {
      if (e.target.closest('[contenteditable="true"]') || e.target.closest("hr")) return;

      const blockEl = e.target.closest("[data-note-block-index]");
      if (blockEl) {
        const index = Number(blockEl.dataset.noteBlockIndex);
        if (!Number.isNaN(index)) {
          e.preventDefault();
          setFocusedBlock(index);
          focusBlockEditor(index, { atEnd: true });
        }
        return;
      }

      if (e.target === editorContainerRef.current || e.target.closest("[data-testid='note-editor-tail']")) {
        e.preventDefault();
        focusAtEnd();
      }
    },
    [focusAtEnd, focusBlockEditor, setFocusedBlock],
  );

  const handleEditorKeyDown = useCallback(
    (e) => {
      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        deleteSelectedBlocks()
      ) {
        e.preventDefault();
        return;
      }

      if (e.shiftKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        const step = e.key === "ArrowUp" ? -1 : 1;
        const nextIndex = Math.min(
          Math.max((blockSelection?.end ?? focusedIndexRef.current) + step, 0),
          blocksRef.current.length - 1,
        );
        if (nextIndex !== focusedIndexRef.current || blockSelection) {
          e.preventDefault();
          extendBlockSelection(nextIndex);
          setFocusedBlock(nextIndex);
          focusBlockEditor(nextIndex, { atEnd: step > 0, cursor: step > 0 ? null : 0 });
        }
        return;
      }

      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
        return;
      }
      if (key === "a" && !e.target.closest('[contenteditable="true"]')) {
        e.preventDefault();
        selectAllBlocks();
        return;
      }
      if (key === "b") {
        e.preventDefault();
        applyInlineFormat("bold");
        return;
      }
      if (key === "i") {
        e.preventDefault();
        applyInlineFormat("italic");
      }
    },
    [applyInlineFormat, deleteSelectedBlocks, extendBlockSelection, focusBlockEditor, blockSelection, redo, selectAllBlocks, setFocusedBlock, undo],
  );

  useEffect(() => {
    syncingRef.current = true;
    historyRef.current.clear();
    setBlocks(parseNoteContent(value));
    setFocusedIndex(0);
    selectionAnchorRef.current = 0;
    setBlockSelection(null);
    syncingRef.current = false;
  }, [noteId]);

  useEffect(() => {
    const root = editorContainerRef.current;
    const serialized = serializeNoteContent(blocksRef.current);
    if ((value || "") === serialized) return;

    // Never clobber live edits while the editor is focused (prevents paste/enter duplication).
    if (root?.contains(document.activeElement)) return;

    setBlocks((prev) => {
      const currentSerialized = serializeNoteContent(prev);
      if ((value || "") === currentSerialized) return prev;
      syncingRef.current = true;
      const next = parseNoteContent(value);
      queueMicrotask(() => {
        syncingRef.current = false;
      });
      return next;
    });
  }, [value]);

  const mergeWithPreviousBlock = useCallback(
    (index, { plainText, atStart, field = "text" }) => {
      setBlocks((prev) => {
        if (index <= 0) {
          const first = unformatFirstEmptyBlock(prev, plainText);
          if (!first) return prev;
          historyRef.current.push(prev);
          emitChange(first.blocks);
          focusedIndexRef.current = first.focusIndex;
          setFocusedIndex(first.focusIndex);
          focusBlockEditor(first.focusIndex, { cursor: first.cursor ?? 0 });
          return first.blocks;
        }

        const result = mergeBlockWithPrevious(prev, index, plainText, { atStart, field });
        if (!result) return prev;

        historyRef.current.push(prev);
        emitChange(result.blocks);
        focusedIndexRef.current = result.focusIndex;
        setFocusedIndex(result.focusIndex);
        if (result.cursor != null) {
          focusBlockEditor(result.focusIndex, { cursor: result.cursor });
        } else {
          focusBlockEditor(result.focusIndex);
        }
        return result.blocks;
      });
    },
    [emitChange, focusBlockEditor],
  );

  const updateBlock = useCallback(
    (index, block) => {
      clearBlockSelection();
      scheduleTypingHistory();
      setBlocks((prev) => {
        let nextBlock = block;
        if (block.type === "todo" && !(block.text || "").trim()) {
          nextBlock = { type: "paragraph", text: "" };
        }
        const next = prev.map((b, i) => (i === index ? nextBlock : b));
        emitChange(next);
        return next;
      });
    },
    [clearBlockSelection, emitChange, scheduleTypingHistory],
  );

  const unformatBlock = useCallback(
    (index) => {
      commitBlocks(
        (prev) => {
          const block = prev[index];
          if (!block) return prev;

          if (block.type === "divider") {
            const next = prev.filter((_, i) => i !== index);
            return next.length ? next : [{ type: "paragraph", text: "" }];
          }
          if (block.type === "toggle") {
            const text = [block.title, block.body].filter(Boolean).join("\n").trim();
            return prev.map((b, i) => (i === index ? { type: "paragraph", text } : b));
          }
          if (block.type === "todo") {
            return prev.map((b, i) => (i === index ? { type: "paragraph", text: block.text || "" } : b));
          }
          if (block.type !== "paragraph") {
            return prev.map((b, i) => (i === index ? { type: "paragraph", text: block.text || "" } : b));
          }
          if (prev.length > 1) return prev.filter((_, i) => i !== index);
          return prev;
        },
        { focusIndex: index },
      );
    },
    [commitBlocks],
  );

  const applyFormat = useCallback(
    (type) => {
      const root = editorContainerRef.current;
      const blockRange = getFormatBlockRange(root, blockSelection, focusedIndexRef.current);
      const editorCtx = getActiveEditorContext(root);
      const multiBlock = blockRange.end > blockRange.start;

      setBlocks((prev) => {
        historyRef.current.push(prev);
        const safeStart = Math.min(Math.max(blockRange.start, 0), Math.max(prev.length - 1, 0));
        const safeEnd = Math.min(Math.max(blockRange.end, 0), Math.max(prev.length - 1, 0));
        let next = [...prev];
        let nextFocus = safeEnd;

        if (type === "divider") {
          const safeIdx = getActiveBlockIndex(root, focusedIndexRef.current);
          const current = next[safeIdx] ?? { type: "paragraph", text: "" };
          if (isEmptyBlock(current)) {
            next = next.map((b, i) => (i === safeIdx ? createEmptyBlock("divider") : b));
            nextFocus = safeIdx;
          } else {
            next.splice(safeIdx + 1, 0, createEmptyBlock("divider"));
            nextFocus = safeIdx + 1;
          }
        } else {
          for (let i = safeEnd; i >= safeStart; i -= 1) {
            const block = next[i];
            if (!block || block.type === "divider") continue;

            const field = editorCtx?.blockIndex === i ? editorCtx.field : "text";
            const source = blockEditableSource(block, field);
            let replacement = null;

            if (blockHasMultipleLines(block, field)) {
              if (editorCtx?.blockIndex === i && editorCtx.el) {
                const offsets = getMarkdownSelectionOffsets(editorCtx.el);
                const { startLine, endLine } = getSelectedLineRangeInText(
                  source,
                  offsets?.start ?? 0,
                  offsets?.end ?? offsets?.start ?? 0,
                );
                replacement = formatSelectedLinesInBlock(block, type, startLine, endLine, field);
              } else if (multiBlock) {
                replacement = formatEntireBlockAsLines(block, type, field);
              } else {
                replacement = [convertBlockType(block, type)];
              }
            } else {
              replacement = [convertBlockType(block, type)];
            }

            next = replaceBlocksSlice(next, i, replacement);
            if (i <= nextFocus) {
              nextFocus = i + Math.max(0, replacement.length - 1);
            }
          }
        }

        emitChange(next);
        focusedIndexRef.current = nextFocus;
        setFocusedIndex(nextFocus);
        focusBlockEditor(nextFocus);
        if (multiBlock) {
          setBlockSelection({ start: safeStart, end: safeEnd });
          selectionAnchorRef.current = safeStart;
        } else {
          clearBlockSelection();
          selectionAnchorRef.current = nextFocus;
        }
        return next;
      });
    },
    [blockSelection, clearBlockSelection, emitChange, focusBlockEditor],
  );

  useImperativeHandle(ref, () => ({ insertBlock: applyFormat }), [applyFormat]);

  const showPlaceholders = isNoteEditorEmpty(blocks);

  const scrollAreaClass = fillContainer
    ? "min-h-0 flex-1 overflow-y-auto overscroll-y-contain space-y-3 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    : "min-h-[14rem] flex-1 overflow-y-auto overscroll-y-contain space-y-3 py-1 sm:min-h-[18rem] md:min-h-[22rem]";

  if (readOnly) {
    const parsed = parseNoteContent(value);
    const readOnlyPlaceholders = isNoteEditorEmpty(parsed);

    return (
      <div
        className={cn(
          fillContainer
            ? "min-h-0 flex-1 overflow-y-auto overscroll-y-contain space-y-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "min-h-[14rem] flex-1 overflow-y-auto overscroll-y-contain space-y-3 sm:min-h-[18rem] md:min-h-[22rem]",
          className,
        )}
        data-testid="note-content-readonly"
      >
        {parsed.map((block, index) => (
          <NoteBlockView
            key={`${noteId}-ro-${index}`}
            block={block}
            index={index}
            blocks={parsed}
            readOnly
            showPlaceholders={readOnlyPlaceholders}
            onFocus={() => {}}
            onChange={() => {}}
            onUnformat={() => {}}
            onMergePrevious={() => {}}
            onEnter={() => {}}
            onPaste={() => {}}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex min-h-0 min-w-0 flex-col gap-3", fillContainer && "flex-1 overflow-hidden", className)} data-testid="note-body-editor">
      {toolbar}
      <div
        ref={editorContainerRef}
        className={cn("flex cursor-text flex-col space-y-3", scrollAreaClass)}
        data-testid="note-content-editor"
        onMouseDown={handleEditorMouseDown}
        onKeyDown={handleEditorKeyDown}
      >
        {blocks.map((block, index) => (
          <div
            key={`${noteId}-${index}-${block.type}`}
            data-note-block-index={index}
            className={cn(
              "cursor-text rounded-[var(--t-event-pill-radius)]",
              block.type === "todo" && index > 0 && blocks[index - 1]?.type === "todo" && "-mt-2.5",
              blockSelection && blockIndexInRange(index, blockSelection) && "bg-[rgba(0,30,255,0.06)]",
            )}
            onMouseDown={(e) => handleBlockMouseDown(e, index)}
          >
            <NoteBlockView
              block={block}
              index={index}
              blocks={blocks}
              readOnly={false}
              showPlaceholders={showPlaceholders}
              onFocus={setFocusedBlock}
              onChange={updateBlock}
              onUnformat={unformatBlock}
              onMergePrevious={mergeWithPreviousBlock}
              onEnter={splitBlockAtCursor}
              onPaste={insertPasteAtCursor}
              onNavigate={navigateFromBlock}
              editorCommands={editorCommands}
            />
          </div>
        ))}
        {fillContainer ? null : <div className="min-h-[4rem] flex-1 shrink-0" data-testid="note-editor-tail" aria-hidden />}
      </div>
    </div>
  );
});
